import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AirflowDagSource } from '../../types';

/*
 * Loads a DAG's source code via its file_token — Airflow has no DAG-versioning REST
 * concept, so there's exactly one current source per DAG
 */
export function useCodeTab(dagId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [source, setSource] = useState<AirflowDagSource | null>(null);
    const [editedContent, setEditedContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /*
     * Fetches the DAG to obtain its file_token, then fetches the source itself
     */
    const fetchSource = useCallback(async () => {
        setIsLoading(true);
        try {
            const dag = await AirflowDashboardHandler.getDag(Env, dagId);
            if (!dag.file_token) throw new Error('DAG has no file_token');
            const response = await AirflowDashboardHandler.getDagSource(Env, dag.file_token);
            setSource(response);
            setEditedContent(response.content);
        } catch {
            errorAlert('Failed to load DAG source.');
        } finally {
            setIsLoading(false);
        }
    }, [Env, dagId]);

    useEffect(() => {
        fetchSource();
    }, [fetchSource, refreshKey]);

    /*
     * Copies the current source content to the clipboard
     */
    const handleCopy = useCallback(() => {
        if (!source) return;
        navigator.clipboard.writeText(source.content);
        successAlert('Copied DAG source to clipboard.');
    }, [source]);

    /*
     * Updates the in-progress edited content as the user types
     */
    const handleContentChange = useCallback((value: string) => {
        setEditedContent(value);
    }, []);

    /*
     * Pushes the edited source via the update API and surfaces the BE's own error message on
     * failure — the push endpoint has no meaningful response body, so the local content is
     * applied optimistically rather than waiting on a round-tripped value
     */
    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        try {
            await AirflowDashboardHandler.pushDagSource(Env, dagId, editedContent);
            setSource((previous) => (previous ? { ...previous, content: editedContent } : previous));
            successAlert('Code updated. It may take 10-15 minutes to reflect the changes.');
        } catch (error: any) {
            errorAlert(error?.response?.data?.message ?? error?.response?.data?.detail ?? 'Failed to update DAG source.');
        } finally {
            setIsSubmitting(false);
        }
    }, [Env, dagId, editedContent]);

    return { source, isLoading, handleCopy, editedContent, isSubmitting, handleContentChange, handleSubmit };
}
